import { createAction, createAsyncThunk } from "@reduxjs/toolkit";
import axiosInstance from "../../axiosInstance/axiosInstance";
import { ApiErrorResponse } from "../../../api/types/ApiErrorResponse";
import { toApiError } from "../../../api/utils/errorHandling";
import { PaginatedResult } from "../../sessions/contracts/PaginatedResult";
import { prepareImage, preparedFileName } from "../../messages/utils/prepareImage";
import { putWithProgress } from "../../messages/utils/putWithProgress";
import { BeginMaterialUploadRequest } from "../contracts/BeginMaterialUploadRequest";
import { MaterialDownloadUrlResponse } from "../contracts/MaterialDownloadUrlResponse";
import { MaterialUploadResponse } from "../contracts/MaterialUploadResponse";
import { MaterialResponse } from "../contracts/MaterialResponse";
import { UpdateMaterialRequest } from "../contracts/UpdateMaterialRequest";
import { Material } from "../models/Material";
import { MaterialResponseMapper } from "../mappers/MaterialResponseMapper";
import { isResizedImageType, materialContentTypeOf, maxBytesFor } from "../utils/materialUploadRules";

const mapper = new MaterialResponseMapper();

/** The instructor list's audience lens: everyone-only, one group, or "what member X sees". */
export interface MaterialAudienceFilter {
  kind: "all" | "everyone" | "group" | "member";
  id?: string;
}

/** Loads a page of the organization's materials, optionally filtered by category/search/audience. */
export const fetchMaterials = createAsyncThunk<
  { items: Material[]; totalCount: number; page: number },
  {
    category?: string;
    search?: string;
    audience?: MaterialAudienceFilter;
    page?: number;
    pageSize?: number;
  },
  { rejectValue: ApiErrorResponse }
>("materials/fetch", async (
  { category, search, audience, page = 1, pageSize = 50 },
  { rejectWithValue },
) => {
  try {
    const response = await axiosInstance.get<PaginatedResult<MaterialResponse>>("/materials", {
      params: {
        category,
        search,
        page,
        pageSize,
        audienceEveryoneOnly: audience?.kind === "everyone" ? true : undefined,
        audienceGroupId: audience?.kind === "group" ? audience.id : undefined,
        audienceMemberId: audience?.kind === "member" ? audience.id : undefined,
      },
    });
    return {
      items: mapper.toMaterials(response.data.items),
      totalCount: response.data.totalCount,
      page: response.data.page,
    };
  } catch (error: unknown) {
    return rejectWithValue(toApiError(error, "Failed to load materials"));
  }
});

export const fetchMaterialCategories = createAsyncThunk<
  string[],
  void,
  { rejectValue: ApiErrorResponse }
>("materials/fetchCategories", async (_, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.get<string[]>("/materials/categories");
    return response.data;
  } catch (error: unknown) {
    return rejectWithValue(toApiError(error, "Failed to load categories"));
  }
});

/** The share (0–1) of the file's bytes the bucket has received so far. */
export const materialUploadProgressed = createAction<number>("materials/uploadProgressed");

/** The bytes to send: a photo resized and re-encoded (no Exif leaves the device), anything else as picked. */
const prepareUpload = async (file: File): Promise<{ blob: Blob; contentType: string; fileName: string }> => {
  const declared = materialContentTypeOf(file);
  if (!isResizedImageType(declared)) {
    return { blob: file, contentType: declared, fileName: file.name };
  }
  const image = await prepareImage(file);
  return { blob: image.blob, contentType: image.blob.type, fileName: preparedFileName(file.name, image.blob.type) };
};

/**
 * Uploads a file straight to the bucket: begin (the API stores an invisible material and signs a PUT
 * for the exact length and type) → PUT with progress → complete (the API checks what arrived and
 * makes it visible). A failed PUT leaves nothing visible; the server purges the pending row.
 */
export const uploadMaterial = createAsyncThunk<
  Material,
  {
    file: File;
    category?: string;
    description?: string;
    notifyMembers?: boolean;
    memberIds?: string[];
    groupIds?: string[];
  },
  { rejectValue: ApiErrorResponse }
>("materials/upload", async (
  { file, category, description, notifyMembers, memberIds, groupIds },
  { dispatch, rejectWithValue },
) => {
  try {
    const { blob, contentType, fileName } = await prepareUpload(file);
    if (blob.size > maxBytesFor(contentType)) {
      return rejectWithValue({ message: "The file is too large.", code: "tooLarge" });
    }
    const restricted = memberIds !== undefined || groupIds !== undefined;
    const request: BeginMaterialUploadRequest = {
      fileName,
      contentType,
      sizeBytes: blob.size,
      category: category || null,
      description: description || null,
      shareWithEveryone: !restricted,
      memberIds: restricted ? memberIds ?? [] : null,
      groupIds: restricted ? groupIds ?? [] : null,
    };
    dispatch(materialUploadProgressed(0));
    const begun = await axiosInstance.post<MaterialUploadResponse>("/materials/uploads", request);
    await putWithProgress(begun.data.uploadUrl, blob, contentType, (fraction) =>
      dispatch(materialUploadProgressed(fraction)),
    );
    const completed = await axiosInstance.post<MaterialResponse>(
      `/materials/uploads/${begun.data.materialId}/complete`,
      undefined,
      { params: notifyMembers ? { notifyMembers: true } : undefined },
    );
    return mapper.toMaterial(completed.data);
  } catch (error: unknown) {
    return rejectWithValue(toApiError(error, "Failed to upload the file"));
  }
});

export const updateMaterial = createAsyncThunk<
  Material,
  { id: string; request: UpdateMaterialRequest },
  { rejectValue: ApiErrorResponse }
>("materials/update", async ({ id, request }, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.put<MaterialResponse>(`/materials/${id}`, request);
    return mapper.toMaterial(response.data);
  } catch (error: unknown) {
    return rejectWithValue(toApiError(error, "Failed to update the material"));
  }
});

/** Deletes a material. Returns the removed material's id. */
export const deleteMaterial = createAsyncThunk<string, string, { rejectValue: ApiErrorResponse }>(
  "materials/delete",
  async (materialId, { rejectWithValue }) => {
    try {
      await axiosInstance.delete(`/materials/${materialId}`);
      return materialId;
    } catch (error: unknown) {
      return rejectWithValue(toApiError(error, "Failed to delete the material"));
    }
  },
);

/** Fetches a short-lived presigned URL and hands the file to the browser. */
export const downloadMaterial = createAsyncThunk<
  string,
  string,
  { rejectValue: ApiErrorResponse }
>("materials/download", async (materialId, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.get<MaterialDownloadUrlResponse>(
      `/materials/${materialId}/download-url`,
    );
    // The presigned URL carries an attachment content-disposition, so a plain
    // navigation saves the file under its original name.
    window.location.assign(response.data.url);
    return materialId;
  } catch (error: unknown) {
    return rejectWithValue(toApiError(error, "Failed to download the file"));
  }
});
